import { MemoryTwoTone, SettingsRemoteTwoTone } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { SensorId, SensorPool } from '@/types/domain/sensor'
import { VirtualSensorPool } from '@/types/domain/virtualsensor'

import { SORT_ATTR_NAME } from '../../api'
import {
  AM,
  AMItem,
  HStack,
  KPage,
  KTextField,
  Preloader,
} from '../../components'
import {
  useSensorPool,
  useVirtualSensorApi,
  useVirtualSensorPool,
} from '../../hooks'

export const EditVSensor = () => {
  const navigate = useNavigate()

  const { id } = useParams()
  const sId: SensorId = Number(id)

  const sPool: SensorPool = useSensorPool()

  const {
    remove: removeVirtualSensor,
    save: saveVirtualSensor,
    update: updateVirtualSensor,
  } = useVirtualSensorApi()

  const virtualSensorPool: VirtualSensorPool = useVirtualSensorPool()

  const [actionMenu, setActionMenu] = useState(null)
  const [name, setName] = useState('')
  const [virtualSensorType, setVirtualSensorType] = useState('')
  const [originalSensorId, setOriginalSensorId] = useState<any[]>([])

  const [combineId, setCombineId] = useState<number>()
  const [combinedVirtualSensorId, setCombinedVirtualSensorId] =
    useState<number>()
  const [combinedVirtualSensorDeviceId, setCombinedVirtualSensorDeviceId] =
    useState('')

  useEffect(() => {
    if (virtualSensorPool?.length === 0) return
    const vRecord = virtualSensorPool.filter(f => Number(f.id) === sId)

    if (vRecord && vRecord[0]) {
      const {
        original_sensors_id,
        virtual_sensor_type,
        combine_id,
        combined_virtual_sensor_id,
        combined_virtual_sensor_device_id,
      } = vRecord[0].attributes

      const dbSensor = sPool.find(
        s => s.attributes.entity_id === combined_virtual_sensor_id
      )

      const { name } = dbSensor.attributes
      const array = original_sensors_id.replaceAll(/\s/g, '').split(',')
      const sensorsArray = []

      array.forEach(s => {
        const actualSensor = sPool.find(f => f.id === s)

        if (actualSensor) {
          sensorsArray.push({
            value: actualSensor.attributes.entity_id,
            label: `${actualSensor.attributes.name}:${
              actualSensor.attributes.device_id
            }:${actualSensor.attributes.sensor_type}`,
            sensor_type: `${actualSensor.attributes.sensor_type}`,
          })
        }
      })

      setName(name)
      setVirtualSensorType(virtual_sensor_type)
      setOriginalSensorId(sensorsArray)
      setCombineId(combine_id)
      setCombinedVirtualSensorId(combined_virtual_sensor_id)
      setCombinedVirtualSensorDeviceId(combined_virtual_sensor_device_id)
    }
  }, [sId, sPool, virtualSensorPool.length, virtualSensorPool])

  if (originalSensorId.length > 0 && virtualSensorType === '')
    setVirtualSensorType(originalSensorId[0].sensor_type)

  const sensorList = sPool ? sPool.filter(f => f.attributes.name !== null) : []

  if (sensorList.length > 0) sensorList.sort(SORT_ATTR_NAME)

  const isPageLoading = virtualSensorPool.length === 0

  return (
    <>
      {/* preloader */}
      <Preloader isLoading={isPageLoading} />
      {/* preloader end */}

      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={'Sensoren'}
        onClose={() => setActionMenu(null)}
        open={Boolean(actionMenu)}
        historyUrlTarget={'virtualsensors'}
      >
        <AMItem
          caption={'Neu'}
          icon={<MemoryTwoTone />}
          onClick={() => navigate('/virtualsensors/new')}
        />

        <Divider />
      </AM>
      {/* action menu end */}

      {!isPageLoading && (
        <KPage
          avatar={
            <Avatar>
              <SettingsRemoteTwoTone />
            </Avatar>
          }
          title={`Virtueller Sensor ${sId ? 'bearbeiten' : 'hinzufügen'}`}
        >
          <HStack sx={{ width: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Sensoren</InputLabel>
              <Select
                autoWidth
                multiple
                onChange={event =>
                  setOriginalSensorId(event.target.value as unknown as any[])
                }
                renderValue={selected => (
                  <Box sx={{ flexWrap: 'wrap', display: 'flex' }}>
                    {selected.map(value => (
                      <Chip
                        key={value.label}
                        label={value.label}
                        onDelete={() => {
                          setOriginalSensorId(chips =>
                            chips.filter(f => f.value !== value.value)
                          )
                        }}
                        onMouseDown={event => event.stopPropagation()}
                      />
                    ))}
                  </Box>
                )}
                value={originalSensorId || ''}
              >
                {sensorList.map(item => {
                  const { device_id, name, sensor_type } = item.attributes

                  if (
                    name &&
                    sensor_type !== 'temperature' &&
                    sensor_type !== 'co2' &&
                    sensor_type !== 'humidity' &&
                    sensor_type !== 'light' &&
                    sensor_type !== 'waterdetector' &&
                    sensor_type !== 'waterdetected' &&
                    sensor_type !== 'vdd' &&
                    sensor_type !== 'motion'
                  ) {
                    return (
                      !originalSensorId
                        .map(s => Number(s.value))
                        .includes(Number(item.id)) && (
                        <MenuItem
                          key={name}
                          /*
                          value={{
                            value: item.id,
                            label: `${name}:${device_id}:${sensor_type}`,
                            sensor_type: `${sensor_type}`,
                          }}
                          */
                        >
                          {name}:{device_id}:{sensor_type}
                        </MenuItem>
                      )
                    )
                  }

                  return null
                })}
              </Select>
            </FormControl>

            <KTextField
              label={'Name'}
              onChange={event => setName(event.target.value)}
              value={name}
            />
          </HStack>

          {!sId ? (
            <Button
              onClick={() => {
                const sensors = []
                originalSensorId.forEach(s => sensors.push(s.value))
                const sensorsString = sensors.join(', ')

                saveVirtualSensor({
                  originalSensorId: sensorsString,
                  name,
                  sensor_type: virtualSensorType,
                })
              }}
            >
              Hinzufügen
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  const sensors = []
                  originalSensorId.forEach(s => sensors.push(s.value))
                  const sensorsString = sensors.join(',')

                  updateVirtualSensor({
                    originalSensorId: sensorsString,
                    name,
                    sensor_type: virtualSensorType,
                    combine_id: combineId,
                    combined_virtual_sensor_id: combinedVirtualSensorId,
                    combined_virtual_sensor_device_id:
                      combinedVirtualSensorDeviceId,
                  })
                }}
              >
                Aktualisieren
              </Button>

              <Button
                color={'secondary'}
                onClick={() => {
                  removeVirtualSensor(sId)
                  navigate('/virtualsensors')
                }}
              >
                Löschen
              </Button>
            </>
          )}
        </KPage>
      )}
    </>
  )
}
